/**
 * Comment editor. Wraps RichTextEditor with an optional `@` mention
 * trigger backed by the caller's org-member list.
 */

import React from 'react';
import { RichTextEditor, type SuggestionTrigger, type TokenPattern } from '@chatsdet/ui';
import { formatMention, mentionRegex, nodeRefRegex, parseMentions, parseNodeRefs } from '@chatsdet/types';
import { FlowArrow } from '@phosphor-icons/react';
import {
  createMentionSuggestionRenderer,
  type MentionSuggestionItem,
} from './MentionSuggestionPopover';

export interface MentionableMember {
  userId: string;
  displayName: string | null;
  primaryEmail: string | null;
}

interface CommentEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  rows?: number;
  mentionableMembers?: MentionableMember[];
  onSubmit?: () => void;
  ariaLabel?: string;
}

const MENTION_TOKEN_PATTERN: TokenPattern = {
  regex: mentionRegex(),
  type: 'mention',
};

/** Node-reference tokens round-trip into chips on load, but users cannot
 * hand-author them — no `SuggestionTrigger` is registered for this pattern. */
const NODE_TOKEN_PATTERN: TokenPattern = {
  regex: nodeRefRegex(),
  type: 'node',
};

const MENTION_TOKEN_COLORS = {
  mention: {
    bg: 'bg-primary/10 dark:bg-primary/20',
    text: 'text-primary',
    border: 'border-primary/30',
  },
  node: {
    bg: 'bg-cyan-500/10 dark:bg-cyan-500/20',
    text: 'text-cyan-700 dark:text-cyan-400',
    border: 'border-cyan-500/30',
  },
};

function renderMentionOrNodeToken(
  attrs: { text: string; tokenType: string },
  colors: { bg: string; text: string; border: string }
): React.ReactNode {
  if (attrs.tokenType === 'node') {
    const parsed = parseNodeRefs(attrs.text);
    const label = parsed[0]?.label ?? attrs.text;
    return (
      <span
        className={`inline-flex items-center gap-1 px-1 py-0.5 rounded text-xs font-medium border cursor-default select-none ${colors.bg} ${colors.text} ${colors.border}`}
      >
        <FlowArrow size={11} weight="bold" />
        {label}
      </span>
    );
  }
  const parsed = parseMentions(attrs.text);
  const displayName = parsed[0]?.displayName ?? attrs.text;
  return (
    <span
      className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium border cursor-default select-none ${colors.bg} ${colors.text} ${colors.border}`}
    >
      @{displayName}
    </span>
  );
}

export function CommentEditor({
  value,
  onChange,
  placeholder,
  isDisabled,
  rows = 3,
  mentionableMembers,
  onSubmit,
  ariaLabel,
}: CommentEditorProps) {
  const members = mentionableMembers ?? [];

  const mentionTrigger = React.useMemo<SuggestionTrigger | null>(() => {
    if (members.length === 0) return null;
    return {
      char: '@',
      items: (query: string) => {
        const needle = query.toLowerCase().trim();
        const candidates = members.filter((m) => {
          if (!needle) return true;
          const name = (m.displayName ?? '').toLowerCase();
          const email = (m.primaryEmail ?? '').toLowerCase();
          return name.includes(needle) || email.includes(needle);
        });
        return candidates.slice(0, 8).map<MentionSuggestionItem>((m) => ({
          id: m.userId,
          label: m.displayName ?? m.primaryEmail ?? m.userId,
          userId: m.userId,
          displayName: m.displayName ?? m.primaryEmail ?? 'Unknown',
          primaryEmail: m.primaryEmail,
        }));
      },
      render: createMentionSuggestionRenderer,
      onSelect: (item) => {
        const mention = item as MentionSuggestionItem;
        return {
          text: formatMention(mention.displayName, mention.userId),
          tokenType: 'mention',
        };
      },
      allowSpaces: false,
    };
  }, [members]);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (
        onSubmit &&
        event.key === 'Enter' &&
        event.shiftKey &&
        !isDisabled &&
        value.trim().length > 0
      ) {
        event.preventDefault();
        onSubmit();
      }
    },
    [onSubmit, isDisabled, value]
  );

  return (
    <RichTextEditor
      aria-label={ariaLabel ?? 'Add a comment'}
      placeholder={placeholder ?? 'Share context, notes, or observations…'}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      multiline
      rows={rows}
      tokenPatterns={mentionTrigger ? [MENTION_TOKEN_PATTERN, NODE_TOKEN_PATTERN] : [NODE_TOKEN_PATTERN]}
      tokenColors={MENTION_TOKEN_COLORS}
      renderToken={renderMentionOrNodeToken}
      triggers={mentionTrigger ? [mentionTrigger] : undefined}
      onKeyDown={onSubmit ? handleKeyDown : undefined}
    />
  );
}
